/**
 * O manifesto de um plugin web (docs/spec/web-plugins.md, "Manifesto"): `manifest.json` na raiz
 * do .zip, ou um `<script type="application/rpgdesk+json" id="rpgdesk-manifest">` no arquivo único.
 *
 * A validação é escrita à mão, e não com um esquema genérico, para cada problema sair com uma
 * mensagem e um conserto que uma pessoa (ou uma IA) entende.
 */
import { MANIFEST_VERSION, PACKAGE_LIMITS, SDK_MAJOR, isHtmlPath } from './constants.js';
import { fieldPathProblem } from './fields.js';
import type { Issue } from './issues.js';
import { MESSAGES, type Lang } from './messages.js';

/** Texto, ou um texto por idioma: `{ "pt": "Ficha", "en": "Sheet" }`. */
export type Localized = string | Readonly<Record<string, string>>;

export type PrivateLevel = 'gm' | 'owner';

export type WebDataType = {
  /** o tipo de dado (o `dataType` da biblioteca); começa com o `id` do plugin e um ponto */
  type: string;
  title: Localized;
  /** o HTML da ficha ou da tabela, dentro do pacote */
  entry: string;
  /** partes do documento só do mestre ou só do dono (só nas fichas) */
  private?: Readonly<Record<string, PrivateLevel>>;
};

export type WebhookDecl = { name: string; description: string; example?: unknown };

export type WebManifest = {
  rpgdesk: 1;
  id: string;
  name: Localized;
  version: string;
  author?: string;
  /** texto, ou um por língua, como o `name` */
  description?: Localized;
  sdk: 1;
  sheets: WebDataType[];
  tables: WebDataType[];
  webhooks: WebhookDecl[];
  icon?: string;
};

/** Nome do arquivo de entrada no .zip montado a partir de um arquivo único. */
export const SINGLE_ENTRY = 'index.html';

const PLUGIN_ID = /^[a-z0-9][a-z0-9._-]*$/;
const WEBHOOK_NAME = /^[a-z0-9][a-z0-9_-]*$/;
const VERSION = /^\d{1,6}\.\d{1,6}\.\d{1,6}$/;
const LANG = /^[a-z]{2}(-[A-Za-z]{2})?$/;

/**
 * Compara duas versões `maior.menor.correção`: negativo se `a` é menor, positivo se é maior, 0 se
 * são iguais. Partes que faltam valem 0.
 */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) - (pb[i] ?? 0);
  return 0;
}

/** O texto no idioma pedido: o idioma, depois a variante (`pt-BR`), depois `pt`, `en` e o primeiro. */
export function localize(text: Localized | undefined, lang: string): string {
  if (text === undefined) return '';
  if (typeof text === 'string') return text;
  const base = lang.slice(0, 2);
  const keys = Object.keys(text);
  const pick =
    text[lang] ??
    text[base] ??
    keys.map((k) => (k.slice(0, 2) === base ? text[k] : undefined)).find((v) => v !== undefined) ??
    text.pt ??
    text.en ??
    (keys[0] !== undefined ? text[keys[0]] : undefined);
  return pick ?? '';
}

type Ctx = { issues: Issue[]; file: string; lang: Lang; M: (typeof MESSAGES)[Lang]['manifest'] };

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function err(ctx: Ctx, code: string, message: string, fix?: string): void {
  ctx.issues.push({ level: 'error', code, message, ...(fix ? { fix } : {}), file: ctx.file });
}
function warn(ctx: Ctx, code: string, message: string, fix?: string): void {
  ctx.issues.push({ level: 'warning', code, message, ...(fix ? { fix } : {}), file: ctx.file });
}

function text(ctx: Ctx, value: unknown, field: string, max: number, required: boolean): string | undefined {
  if (value === undefined || value === null) {
    if (required) err(ctx, 'manifest-field', ctx.M.missingField(field), ctx.M.addField(field));
    return undefined;
  }
  if (typeof value !== 'string' || value.trim() === '') {
    err(ctx, 'manifest-field', ctx.M.notText(field));
    return undefined;
  }
  if (value.length > max) {
    err(ctx, 'manifest-field', ctx.M.tooLong(field, max), ctx.M.shorten(field));
    return undefined;
  }
  return value.trim();
}

function localized(ctx: Ctx, value: unknown, field: string, max: number, required: boolean): Localized | undefined {
  if (isObject(value)) {
    const out: Record<string, string> = {};
    for (const [lang, v] of Object.entries(value)) {
      if (!LANG.test(lang)) {
        err(ctx, 'manifest-field', ctx.M.notLang(field, lang), ctx.M.langFix);
        continue;
      }
      const t = text(ctx, v, `${field}.${lang}`, max, true);
      if (t !== undefined) out[lang] = t;
    }
    if (Object.keys(out).length === 0) {
      if (required) err(ctx, 'manifest-field', ctx.M.noText(field), ctx.M.noTextFix(field));
      return undefined;
    }
    return out;
  }
  return text(ctx, value, field, max, required);
}

function dataTypes(ctx: Ctx, value: unknown, key: 'sheets' | 'tables', id: string | undefined, single: boolean): WebDataType[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) {
    err(ctx, 'manifest-field', ctx.M.notList(key));
    return [];
  }
  const out: WebDataType[] = [];
  value.forEach((raw, i) => {
    const where = `${key}[${i}]`;
    if (!isObject(raw)) {
      err(ctx, 'manifest-field', ctx.M.notObject(where));
      return;
    }
    const type = text(ctx, raw.type, `${where}.type`, 120, true);
    if (type !== undefined) {
      if (!PLUGIN_ID.test(type)) {
        err(ctx, 'manifest-type', ctx.M.typeChars(where, type), ctx.M.typeCharsFix(id ?? (ctx.lang === 'pt' ? 'com.seunome.sistema' : 'com.yourname.system')));
      } else if (id !== undefined && !type.startsWith(`${id}.`)) {
        err(ctx, 'manifest-type', ctx.M.typePrefix(where, type, id), ctx.M.typePrefixFix(`${id}.${type.split('.').pop() || ctx.M.defaultType}`));
      }
    }
    const title = localized(ctx, raw.title, `${where}.title`, 120, true);
    let entry: string | undefined;
    if (single) {
      entry = SINGLE_ENTRY;
    } else {
      entry = text(ctx, raw.entry, `${where}.entry`, 300, true);
      if (entry !== undefined && !isHtmlPath(entry)) {
        err(ctx, 'manifest-entry', ctx.M.entryHtml(where, entry));
        entry = undefined;
      }
    }
    let priv: Record<string, PrivateLevel> | undefined;
    if (raw.private !== undefined) {
      if (key === 'tables') {
        warn(ctx, 'manifest-private-table', ctx.M.privateTable(where), ctx.M.privateTableFix);
      } else if (!isObject(raw.private)) {
        err(ctx, 'manifest-field', ctx.M.privateObject(where));
      } else {
        priv = {};
        for (const [path, level] of Object.entries(raw.private)) {
          const problem = fieldPathProblem(path, ctx.lang);
          if (problem) err(ctx, 'manifest-private', `"${where}.private": ${problem}`);
          else if (level !== 'gm' && level !== 'owner') err(ctx, 'manifest-private', ctx.M.privateLevel(where, path));
          else priv[path] = level;
        }
      }
    }
    if (type !== undefined && title !== undefined && entry !== undefined) out.push({ type, title, entry, ...(priv && Object.keys(priv).length ? { private: priv } : {}) });
  });
  return out;
}

function webhooks(ctx: Ctx, value: unknown): WebhookDecl[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) {
    err(ctx, 'manifest-field', ctx.M.webhooksList);
    return [];
  }
  if (value.length > PACKAGE_LIMITS.maxWebhooks) err(ctx, 'manifest-webhooks', ctx.M.webhooksMax(value.length, PACKAGE_LIMITS.maxWebhooks));
  const out: WebhookDecl[] = [];
  const seen = new Set<string>();
  value.forEach((raw, i) => {
    const where = `webhooks[${i}]`;
    if (!isObject(raw)) {
      err(ctx, 'manifest-field', ctx.M.webhookObject(where));
      return;
    }
    const name = text(ctx, raw.name, `${where}.name`, 40, true);
    if (name !== undefined && !WEBHOOK_NAME.test(name)) {
      err(ctx, 'manifest-webhook-name', ctx.M.webhookName(where, name), ctx.M.webhookNameFix);
      return;
    }
    if (name !== undefined && seen.has(name)) {
      err(ctx, 'manifest-webhook-name', ctx.M.webhookRepeated(name));
      return;
    }
    const description = text(ctx, raw.description, `${where}.description`, 300, true);
    if (raw.example !== undefined) {
      let size = Infinity;
      try {
        size = new TextEncoder().encode(JSON.stringify(raw.example)).length;
      } catch {
        // `example` com ciclo não vem de JSON; fica como grande demais
      }
      if (size > PACKAGE_LIMITS.maxExampleBytes) err(ctx, 'manifest-webhook-example', ctx.M.webhookExample(String(name ?? i), PACKAGE_LIMITS.maxExampleBytes), ctx.M.webhookExampleFix);
    }
    if (name !== undefined && description !== undefined) {
      seen.add(name);
      out.push({ name, description, ...(raw.example !== undefined ? { example: raw.example } : {}) });
    }
  });
  return out;
}

/**
 * Confere o manifesto e devolve a versão limpa (ou null, se há erro). `single`: veio de um arquivo
 * único, onde a entrada é o próprio arquivo e há uma ficha ou tabela só.
 */
export function parseManifest(raw: unknown, opts: { single: boolean; file: string; lang?: Lang }): { manifest: WebManifest | null; issues: Issue[] } {
  const lang = opts.lang ?? 'pt';
  const ctx: Ctx = { issues: [], file: opts.file, lang, M: MESSAGES[lang].manifest };
  if (!isObject(raw)) {
    err(ctx, 'manifest-json', ctx.M.notJsonObject);
    return { manifest: null, issues: ctx.issues };
  }
  if (raw.rpgdesk !== MANIFEST_VERSION) {
    err(ctx, 'manifest-version', ctx.M.version(MANIFEST_VERSION), ctx.M.versionFix(MANIFEST_VERSION));
  }
  const id = text(ctx, raw.id, 'id', 120, true);
  if (id !== undefined && !PLUGIN_ID.test(id)) {
    err(ctx, 'manifest-id', ctx.M.id(id), ctx.M.idFix);
  }
  const validId = id !== undefined && PLUGIN_ID.test(id) ? id : undefined;
  const name = localized(ctx, raw.name, 'name', 120, true);
  const version = text(ctx, raw.version, 'version', 40, true);
  if (version !== undefined && !VERSION.test(version)) {
    err(ctx, 'manifest-semver', ctx.M.semver(version));
  }
  const author = text(ctx, raw.author, 'author', 120, false);
  const description = localized(ctx, raw.description, 'description', 1000, false);
  if (raw.sdk !== SDK_MAJOR) {
    err(ctx, 'manifest-sdk', ctx.M.sdk(SDK_MAJOR), ctx.M.sdkFix(SDK_MAJOR));
  }
  const sheets = dataTypes(ctx, raw.sheets, 'sheets', validId, opts.single);
  const tables = dataTypes(ctx, raw.tables, 'tables', validId, opts.single);
  const total = (Array.isArray(raw.sheets) ? raw.sheets.length : 0) + (Array.isArray(raw.tables) ? raw.tables.length : 0);
  if (total === 0) {
    err(ctx, 'manifest-empty', ctx.M.empty, ctx.M.emptyFix);
  } else if (opts.single && total > 1) {
    err(ctx, 'manifest-single', ctx.M.single, ctx.M.singleFix);
  } else if (total > PACKAGE_LIMITS.maxTypes) {
    err(ctx, 'manifest-types', ctx.M.types(total, PACKAGE_LIMITS.maxTypes));
  }
  const types = [...sheets, ...tables].map((d) => d.type);
  for (const [i, type] of types.entries()) {
    if (types.indexOf(type) !== i) err(ctx, 'manifest-type-repeated', ctx.M.typeRepeated(type), ctx.M.typeRepeatedFix);
  }
  const hooks = webhooks(ctx, raw.webhooks);
  let icon: string | undefined;
  if (raw.icon !== undefined) {
    icon = text(ctx, raw.icon, 'icon', 300, false);
    if (icon !== undefined && !/\.(png|svg)$/i.test(icon)) {
      err(ctx, 'manifest-icon', ctx.M.icon(icon));
      icon = undefined;
    }
  }
  for (const key of Object.keys(raw)) {
    if (!['$schema', 'rpgdesk', 'id', 'name', 'version', 'author', 'description', 'sdk', 'sheets', 'tables', 'webhooks', 'icon'].includes(key)) {
      warn(ctx, 'manifest-unknown', ctx.M.unknown(key));
    }
  }
  if (ctx.issues.some((i) => i.level === 'error') || id === undefined || name === undefined || version === undefined) return { manifest: null, issues: ctx.issues };
  return {
    manifest: {
      rpgdesk: 1,
      id,
      name,
      version,
      ...(author ? { author } : {}),
      ...(description ? { description } : {}),
      sdk: 1,
      sheets,
      tables,
      webhooks: hooks,
      ...(icon ? { icon } : {}),
    },
    issues: ctx.issues,
  };
}
